import { useState, useMemo } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  CheckCircle,
  ArrowLeft,
  Loader2,
  ExternalLink,
  Plus,
  Trash2,
  Pencil,
  AlertTriangle,
} from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { useAuth } from '@/hooks/useAuth'
import athleteApi from '@/lib/api'
import { validateSocialMediaUrl, SUPPORTED_PLATFORMS } from '@adthlete/common'
import type { DeliverableItem } from '@adthlete/shared-frontend'

const CampaignLive = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { campaign: collaboration, isLoading: detailLoading, refetch } = useCampaignDetail(id)
  const { athlete } = useAuth()

  // Free-form links (no structured deliverables)
  const [postLinks, setPostLinks] = useState<string[]>([''])
  // Per-deliverable links keyed by deliverable ID
  const [deliverableLinks, setDeliverableLinks] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // Find the Campaign Live milestone
  const collaborationLiveMilestone = useMemo(() => {
    if (!collaboration?.milestones) return null
    return collaboration.milestones.find((m) =>
      m.name.toLowerCase().includes('collaboration live'),
    )
  }, [collaboration?.milestones])

  const isComplete = useMemo(() => {
    return (
      collaborationLiveMilestone?.status === 'approved' ||
      collaborationLiveMilestone?.status === 'completed'
    )
  }, [collaborationLiveMilestone])

  const structuredDeliverables: DeliverableItem[] | null =
    (collaboration?.structuredDeliverables as DeliverableItem[] | null | undefined) ?? null
  const hasDeliverables =
    Array.isArray(structuredDeliverables) && structuredDeliverables.length > 0

  const missingPlatforms = useMemo(() => {
    if (!collaboration || !athlete) return []
    const connectedPlatforms = new Set(
      (athlete.socialAccounts ?? []).map((a) => a.platform.toLowerCase()),
    )
    const requiredPlatforms: string[] =
      hasDeliverables && structuredDeliverables
        ? [...new Set(structuredDeliverables.map((d) => d.platform.toLowerCase()))]
        : (collaboration.platform ?? []).map((p: string) => p.toLowerCase())
    return requiredPlatforms.filter((p) => !connectedPlatforms.has(p))
  }, [collaboration, athlete, hasDeliverables, structuredDeliverables])

  // ── Free-form helpers ──────────────────────────────────────────────────────
  const addLink = () => setPostLinks((prev) => [...prev, ''])
  const removeLink = (index: number) => setPostLinks((prev) => prev.filter((_, i) => i !== index))
  const updateLink = (index: number, value: string) =>
    setPostLinks((prev) => prev.map((l, i) => (i === index ? value : l)))

  // ── Start editing ──────────────────────────────────────────────────────────
  const startEditing = () => {
    if (hasDeliverables && structuredDeliverables) {
      // Pre-fill from saved links in deliverable order
      const saved = collaboration?.postLinks ?? []
      const prefilled: Record<string, string> = {}
      structuredDeliverables.forEach((d, i) => {
        prefilled[d.id] = saved[i] ?? ''
      })
      setDeliverableLinks(prefilled)
    } else {
      setPostLinks(
        collaboration?.postLinks && collaboration.postLinks.length > 0
          ? [...collaboration.postLinks]
          : [''],
      )
    }
    setIsEditing(true)
  }

  if (detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C0FD72]" />
      </div>
    )
  }

  if (!collaboration) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4 text-white">Collaboration Not Found</h2>
        <p className="mb-4 text-gray-300">The collaboration you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  const handleSubmit = async () => {
    let validLinks: string[]

    if (hasDeliverables && structuredDeliverables) {
      // Collect in deliverable order; allow blank for sameAs (will still be submitted as empty)
      validLinks = structuredDeliverables.map((d) => (deliverableLinks[d.id] ?? '').trim())
      const atLeastOne = validLinks.some(Boolean)
      if (!atLeastOne) {
        toast.error('Link required', {
          description: 'Please enter at least one post link.',
        })
        return
      }
      // Validate non-empty links
      for (let i = 0; i < validLinks.length; i++) {
        const link = validLinks[i]
        if (!link) continue
        const result = validateSocialMediaUrl(link)
        if (result.valid === false) {
          const d = structuredDeliverables[i]
          const label = `#${i + 1} ${d.platform} ${d.format}`
          if (result.reason === 'invalid_url') {
            toast.error('Invalid URL', { description: `${label}: "${link}" is not a valid URL` })
          } else {
            toast.error('Unsupported platform', {
              description: `${label}: doesn't appear to be a ${SUPPORTED_PLATFORMS.join(', ')} link`,
            })
          }
          return
        }
      }
    } else {
      validLinks = postLinks.map((l) => l.trim()).filter(Boolean)
      if (validLinks.length === 0) {
        toast.error('Link Required', {
          description: 'Please enter at least one link to your published content',
        })
        return
      }
      for (const link of validLinks) {
        const result = validateSocialMediaUrl(link)
        if (result.valid === false) {
          if (result.reason === 'invalid_url') {
            toast.error('Invalid URL', {
              description: `"${link}" is not a valid URL (e.g., https://tiktok.com/@username/video/123)`,
            })
          } else {
            toast.error('Unsupported platform', {
              description: `"${link}" doesn't appear to be a ${SUPPORTED_PLATFORMS.join(', ')} link.`,
            })
          }
          return
        }
      }
    }

    setSubmitting(true)
    try {
      await athleteApi.patch(`/athlete/collaborations/${id}`, {
        livePostLinks: validLinks,
      })
      toast('Success!', { description: 'Your post link(s) have been submitted' })
      await refetch()
      setIsEditing(false)
    } catch (error) {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to submit post link(s)',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // ── Completed view ─────────────────────────────────────────────────────────
  const renderCompleted = () => {
    const savedLinks = collaboration.postLinks ?? []

    return (
      <div className="text-center py-8">
        <CheckCircle className="h-16 w-16 text-[#C0FD72] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Collaboration is Live! 🎉</h3>
        <p className="text-gray-400 mb-6">
          Your content has been published and the collaboration is now live.
        </p>

        {hasDeliverables && structuredDeliverables ? (
          <div className="flex flex-col gap-2 items-center">
            {structuredDeliverables.map((d, i) => {
              const link = savedLinks[i]
              if (!link) return null
              return (
                <Button
                  key={d.id}
                  variant="outline"
                  className="border-[#C0FD72] text-[#C0FD72] hover:bg-[#C0FD72] hover:text-black"
                  onClick={() => window.open(link, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />#{i + 1} {d.platform} {d.format}
                </Button>
              )
            })}
          </div>
        ) : (
          savedLinks.length > 0 && (
            <div className="flex flex-col gap-2 items-center">
              {savedLinks.map((link, i) => (
                <Button
                  key={i}
                  variant="outline"
                  className="border-[#C0FD72] text-[#C0FD72] hover:bg-[#C0FD72] hover:text-black"
                  onClick={() => window.open(link, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Published Content{savedLinks.length > 1 ? ` (${i + 1})` : ''}
                </Button>
              ))}
            </div>
          )
        )}

        <Button
          variant="ghost"
          size="sm"
          className="mt-4 text-gray-400 hover:text-white hover:bg-white/10"
          onClick={startEditing}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit links
        </Button>
      </div>
    )
  }

  // ── Edit / submit view ─────────────────────────────────────────────────────
  const renderForm = () => (
    <div className="space-y-6">
      {missingPlatforms.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-amber-300 text-sm font-semibold">
              Social account not connected:{' '}
              {missingPlatforms.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}
            </p>
            <p className="text-amber-200/80 text-sm">
              Brands rely on connected social accounts to monitor post performance automatically.
              Without it, reporting must be done manually and may delay your payment.
            </p>
            <Link
              to="/settings?tab=social"
              className="inline-block text-sm font-medium text-amber-300 underline hover:text-amber-200"
            >
              Connect your account in Settings →
            </Link>
          </div>
        </div>
      )}

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <p className="text-blue-300 text-sm">
          <strong>Ready to go live?</strong> Once you've published your content, submit the link(s)
          below so the brand can track performance and complete the collaboration.
        </p>
      </div>

      {hasDeliverables && structuredDeliverables ? (
        <div className="space-y-4">
          <Label className="text-white">Published Content Links</Label>
          {structuredDeliverables.map((d, index) => {
            const sourceIndex = d.sameAsDeliverableId
              ? structuredDeliverables.findIndex((x) => x.id === d.sameAsDeliverableId)
              : -1
            return (
              <div key={d.id} className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  #{index + 1} {d.platform} {d.format}
                  {sourceIndex >= 0 && (
                    <span className="ml-2 text-[#C0FD72]/70 normal-case tracking-normal font-normal">
                      (same content as #{sourceIndex + 1})
                    </span>
                  )}
                </p>
                <Input
                  type="url"
                  autoComplete="url"
                  placeholder={`https://${d.platform.toLowerCase()}.com/...`}
                  value={deliverableLinks[d.id] ?? ''}
                  onChange={(e) =>
                    setDeliverableLinks((prev) => ({ ...prev, [d.id]: e.target.value }))
                  }
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
            )
          })}
        </div>
      ) : (
        <div className="space-y-3">
          <Label className="text-white">Published Content Link(s) *</Label>
          {postLinks.map((link, index) => (
            <div key={index} className="flex gap-2">
              <Input
                type="url"
                autoComplete="url"
                placeholder="https://tiktok.com/@username/video/123456789"
                value={link}
                onChange={(e) => updateLink(index, e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
              />
              {postLinks.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeLink(index)}
                  className="text-gray-400 hover:text-red-400 hover:bg-red-400/10 shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={addLink}
            className="text-[#C0FD72] hover:text-[#C0FD72] hover:bg-[#C0FD72]/10"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add another link
          </Button>
          <p className="text-xs text-gray-400">
            Enter the direct link(s) to your published TikTok, Instagram, or YouTube content
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button onClick={handleSubmit} disabled={submitting} variant="lime" className="flex-1">
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : isEditing ? (
            'Update Link(s)'
          ) : (
            'Submit Link(s)'
          )}
        </Button>
        {isEditing && (
          <Button
            variant="ghost"
            onClick={() => setIsEditing(false)}
            disabled={submitting}
            className="text-gray-400 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-white hover:text-[#C0FD72] hover:bg-white/10"
          onClick={() => navigate(`/collaborations/${id}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Collaboration
        </Button>

        <Card className="bg-gray-900/90 backdrop-blur-xs border border-white/10">
          <CardHeader className="border-b border-white/10">
            <div className="flex items-center gap-3">
              {isComplete ? (
                <CheckCircle className="h-6 w-6 text-[#C0FD72]" />
              ) : (
                <div className="h-6 w-6 rounded-full border-2 border-gray-600" />
              )}
              <div>
                <CardTitle className="text-2xl text-white">Live & Published</CardTitle>
                <p className="text-sm text-gray-400 mt-1">{collaboration.title}</p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {isComplete && !isEditing ? renderCompleted() : renderForm()}
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">
            Need help? Contact support at{' '}
            <a href="mailto:support@adthlete.com" className="text-[#C0FD72] hover:underline">
              support@adthlete.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default CampaignLive
